import { UNSUPPORTED_READABILITY_RUNTIMES } from "@hireroo/app-definition/challenge";
import { ENTITY_REPORT_TOP } from "@hireroo/app-helper/entity";
import { scrollToContentForReportV2 } from "@hireroo/app-helper/html-element";
import { AssessmentReport } from "@hireroo/app-store/widget/t/AssessmentReport";
import { formatScore } from "@hireroo/formatter/score";
import * as Graphql from "@hireroo/graphql/client/urql";
import { useCurrentLanguage, useTranslation } from "@hireroo/i18n";
import { resolveLanguage } from "@hireroo/i18n/utils";
import type { Widget } from "@hireroo/presentation";
import { updateHashParam } from "@hireroo/router/api";
import * as React from "react";

import { generateSmoothTargetId } from "./privateHelper";

type EntityScoreSelector = Widget.AssessmentReportProps["summary"]["entityScoreSelector"];
type ScoreSummaryDetailItem = Exclude<EntityScoreSelector["detailDialog"], undefined>["items"][0];
type ScoreSummaryItem = Widget.AssessmentReportProps["summary"]["entityScoreSelector"]["items"][0];

export type UseGenerateEntityScoreSelectorArgs = {
  reportSettings: Graphql.AssessmentReportSettings | null;
};

export const useGenerateEntityScoreSelector = (_args: UseGenerateEntityScoreSelectorArgs): EntityScoreSelector => {
  const { t } = useTranslation();
  const exam = AssessmentReport.useExam();
  const lang = useCurrentLanguage();
  const selectedEntityId = AssessmentReport.useSelectedEntityId();
  const [forceClose, setForceClose] = React.useState(false);

  const scoreSummaryItems = exam.entities.map((entity): ScoreSummaryItem => {
    if (entity.__typename === "ChallengeEntity" && entity.challengeQuestion) {
      const question = entity.challengeQuestion;
      const _submission = entity.submissions.filter(s => s.questionId === question.questionId && s.isBest)[0];
      const hashKey = generateSmoothTargetId(entity);
      const uniqueEntityId = AssessmentReport.generateUniqueEntityId(entity);
      return {
        id: entity.id,
        title: resolveLanguage(question, lang, "title"),
        // subTitle: submission ? languageMapForDisplay[submission.runtime] : "",
        scoreInteger: formatScore(entity.totalScore),
        difficulty: entity.challengeQuestion.difficulty,
        toggleButton: {
          onClick: () => {
            AssessmentReport.updateSelectedEntityId(uniqueEntityId);
            updateHashParam(hashKey);
            scrollToContentForReportV2(ENTITY_REPORT_TOP);
          },
          selected: selectedEntityId === uniqueEntityId,
        },
        showSuspiciousIcon: false,
      };
    } else if (entity.__typename === "QuizEntity" && entity.pb_package) {
      const pkg = entity.pb_package;
      const hashKey = generateSmoothTargetId(entity);
      const uniqueEntityId = AssessmentReport.generateUniqueEntityId(entity);
      return {
        id: entity.id,
        title: resolveLanguage(pkg, lang, "title"),
        scoreInteger: formatScore(entity.totalScore),
        difficulty: entity.pb_package.difficulty,
        toggleButton: {
          onClick: () => {
            AssessmentReport.updateSelectedEntityId(uniqueEntityId);
            updateHashParam(hashKey);
            scrollToContentForReportV2(ENTITY_REPORT_TOP);
          },
          selected: selectedEntityId === uniqueEntityId,
        },
        showSuspiciousIcon: false,
      };
    } else if (entity.__typename === "ProjectEntity" && entity.question) {
      const question = entity.question;
      const hashKey = generateSmoothTargetId(entity);
      const uniqueEntityId = AssessmentReport.generateUniqueEntityId(entity);
      return {
        id: entity.id,
        title: resolveLanguage(question, lang, "title"),
        scoreInteger: formatScore(entity.totalScore),
        difficulty: entity.question.difficulty,
        toggleButton: {
          onClick: () => {
            AssessmentReport.updateSelectedEntityId(uniqueEntityId);
            updateHashParam(hashKey);
            scrollToContentForReportV2(ENTITY_REPORT_TOP);
          },
          selected: selectedEntityId === uniqueEntityId,
        },
        showSuspiciousIcon: false,
      };
    } else if (entity.__typename === "SystemDesignEntity" && entity.question) {
      const question = entity.question;
      const hashKey = generateSmoothTargetId(entity);
      const uniqueEntityId = AssessmentReport.generateUniqueEntityId(entity);
      return {
        id: entity.id,
        title: resolveLanguage(question, lang, "title"),
        scoreInteger: formatScore(entity.totalScore),
        difficulty: entity.question.difficulty,
        toggleButton: {
          onClick: () => {
            AssessmentReport.updateSelectedEntityId(uniqueEntityId);
            updateHashParam(hashKey);
            scrollToContentForReportV2(ENTITY_REPORT_TOP);
          },
          selected: selectedEntityId === uniqueEntityId,
        },
        showSuspiciousIcon: false,
      };
    } else {
      throw new Error("unknown entity type");
    }
  });

  const scoreSummaryDetailItems = exam.entities.map((entity): ScoreSummaryDetailItem => {
    if (entity.__typename === "ChallengeEntity" && entity.challengeQuestion) {
      const question = entity.challengeQuestion;
      const submission = entity.submissions.filter(s => s.questionId === question.questionId && s.isBest)[0];
      const readability = submission?.readability ? formatScore(submission.readability) : 0;
      const hasReadability = submission?.readabilityTestResult === "" ? false : !UNSUPPORTED_READABILITY_RUNTIMES.includes(submission?.runtime);
      const hashKey = generateSmoothTargetId(entity);
      const uniqueEntityId = AssessmentReport.generateUniqueEntityId(entity);

      return {
        id: uniqueEntityId,
        title: resolveLanguage(question, lang, "title"),
        // subTitle: submission ? languageMapForDisplay[submission.runtime] : "",
        difficulty: entity.challengeQuestion.difficulty,
        variantText: t("コーディング形式"),
        scoreChart: {
          score: formatScore(entity.totalScore),
        },
        scoreBars: [
          {
            title: t("正解率"),
            score: submission?.coverage ? formatScore(submission.coverage) : 0,
            color: "secondary" as const,
          },
          {
            title: t("パフォーマンス"),
            score: submission?.performance ? formatScore(submission.performance) : 0,
            color: "info" as const,
          },
          hasReadability && {
            title: t("可読性"),
            score: readability,
            color: "warning" as const,
          },
        ].flatMap(v => (v ? [v] : [])),
        status: entity.challengeStatus === "EVALUATED" ? "EVALUATED" : "EVALUATING",
        detailButton: {
          onClick: () => {
            updateHashParam(hashKey);
            AssessmentReport.updateSelectedEntityId(uniqueEntityId);
            setForceClose(true);
          },
        },
      };
    } else if (entity.__typename === "QuizEntity" && entity.pb_package) {
      const pkg = entity.pb_package;
      const hashKey = generateSmoothTargetId(entity);
      const uniqueEntityId = AssessmentReport.generateUniqueEntityId(entity);
      return {
        id: uniqueEntityId,
        title: resolveLanguage(pkg, lang, "title"),
        difficulty: entity.pb_package.difficulty,
        variantText: t("クイズ形式"),
        scoreChart: {
          score: formatScore(entity.totalScore),
        },
        scoreBars: [
          {
            title: t("正解率"),
            score: formatScore(entity.totalScore),
            color: "secondary",
          },
        ],
        status: "EVALUATED",
        detailButton: {
          onClick: () => {
            updateHashParam(hashKey);
            AssessmentReport.updateSelectedEntityId(uniqueEntityId);
            setForceClose(true);
          },
        },
      };
    } else if (entity.__typename === "ProjectEntity" && entity.question) {
      const question = entity.question;
      const hashKey = generateSmoothTargetId(entity);
      const uniqueEntityId = AssessmentReport.generateUniqueEntityId(entity);
      return {
        id: uniqueEntityId,
        title: resolveLanguage(question, lang, "title"),
        difficulty: entity.question.difficulty,
        variantText: t("実践形式"),
        scoreChart: {
          score: formatScore(entity.totalScore),
        },
        scoreBars: [],
        status: "EVALUATED",
        detailButton: {
          onClick: () => {
            updateHashParam(hashKey);
            AssessmentReport.updateSelectedEntityId(uniqueEntityId);
            setForceClose(true);
          },
        },
      };
    } else if (entity.__typename === "SystemDesignEntity" && entity.question) {
      const question = entity.question;
      const hashKey = generateSmoothTargetId(entity);
      const uniqueEntityId = AssessmentReport.generateUniqueEntityId(entity);
      return {
        id: entity.id,
        title: resolveLanguage(question, lang, "title"),
        difficulty: entity.question.difficulty,
        variantText: t("システムデザイン形式"),
        scoreChart: {
          score: formatScore(entity.totalScore),
        },
        scoreBars: [],
        status: entity.systemDesignStatus === "EVALUATED" ? "EVALUATED" : "EVALUATING",
        detailButton: {
          onClick: () => {
            updateHashParam(hashKey);
            AssessmentReport.updateSelectedEntityId(uniqueEntityId);
            setForceClose(true);
          },
        },
      };
    } else {
      throw new Error("unknown entity type");
    }
  });

  return {
    detailDialog: {
      items: scoreSummaryDetailItems,
      forceClose: forceClose,
      onClose: () => {
        setForceClose(false);
      },
    },
    items: scoreSummaryItems,
    defaultValue: selectedEntityId || "",
  };
};
